import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function SharedProject() {
  const { token } = useParams();
  const [project, setProject] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [content, setContent] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [compiling, setCompiling] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState('');
  const autoSaveTimer = useRef(null);

  useEffect(() => {
    const userToken = localStorage.getItem('token');
    fetch(`${API_BASE}/api/shared/${token}`)
      .then(r => r.json())
      .then(async data => {
        if (data.success) {
          setProject(data);
          const mainFile = data.files.find(f => f === 'main.tex') || 
                          data.files.find(f => f.endsWith('.tex')) || 
                          data.files[0];
          if (mainFile) loadFile(mainFile, data);
          if (userToken) {
            try {
              await fetch(`${API_BASE}/api/shared/${token}/fork`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${userToken}` }
              });
            } catch(err) { console.log('Fork failed:', err); }
          }
        } else {
          setError('Invalid or expired share link');
        }
      });
  }, [token]);

  const loadFile = (filePath, proj) => {
    setSelectedFile(filePath);
    fetch(`${API_BASE}/api/shared/${token}/file?filePath=${encodeURIComponent(filePath)}`)
      .then(r => r.json())
      .then(data => { if (data.success) setContent(data.content); });
  };

  const saveFile = async (fileContent, filePath) => {
    setSaveStatus('saving');
    await fetch(`${API_BASE}/api/shared/${token}/file`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filePath: filePath || selectedFile, content: fileContent || content })
    });
    setSaveStatus('saved');
    setTimeout(() => setSaveStatus(''), 2000);
  };

  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);
    // Auto-save after 2 seconds
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => saveFile(newContent, selectedFile), 2000);
  };

  const compilePDF = async () => {
    setCompiling(true);
    try {
      const userToken = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (userToken) headers['Authorization'] = `Bearer ${userToken}`;
      
      // Save first before compiling
      await saveFile(content, selectedFile);
      
      const res = await fetch(`${API_BASE}/api/shared/${token}/compile`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ mainFile: selectedFile })
      });
      const data = await res.json();
      if (data.success) {
        const byteChars = atob(data.pdf);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
        setPdfUrl(URL.createObjectURL(blob));
      } else {
        alert('Compilation failed: ' + (data.error || 'Unknown error'));
      }
    } catch(err) { console.error(err); alert('Compilation failed!'); }
    setCompiling(false);
  };

  if (error) return (
    <div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'100vh',flexDirection:'column'}}>
      <h2>❌ {error}</h2>
      <a href="/" style={{color:'#5a6fd6'}}>Go to LaTeXLaB</a>
    </div>
  );

  if (!project) return (
    <div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'100vh'}}>
      <h2>Loading shared project...</h2>
    </div>
  );

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100vh'}}>
      <div style={{padding:'10px 20px',background:'#5a6fd6',color:'white',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <span>📁 {project.projectName} <span style={{fontSize:'12px',opacity:'0.8'}}>(Shared)</span></span>
        <div style={{display:'flex',gap:'10px',alignItems:'center'}}>
          {saveStatus === 'saving' && <span style={{fontSize:'12px'}}>💾 Saving...</span>}
          {saveStatus === 'saved' && <span style={{fontSize:'12px'}}>✓ Saved</span>}
          <button onClick={compilePDF} disabled={compiling} style={{padding:'6px 14px',background:'#c0392b',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:'600'}}>
            {compiling ? '⏳ Compiling...' : '▶️ Compile PDF'}
          </button>
          <button onClick={() => saveFile(content, selectedFile)} style={{padding:'6px 14px',background:'white',color:'#5a6fd6',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:'600'}}>Save</button>
          <a href="/" style={{color:'white',fontSize:'13px'}}>LaTeXLaB Home</a>
        </div>
      </div>
      <div style={{display:'flex',flex:1,overflow:'hidden'}}>
        <div style={{width:'200px',background:'#f8fafc',borderRight:'1px solid #e2e8f0',padding:'10px',overflowY:'auto'}}>
          <div style={{fontSize:'12px',fontWeight:'600',color:'#64748b',marginBottom:'8px'}}>FILES</div>
          {project.files.map(f => (
            <div key={f} onClick={() => loadFile(f, project)}
              style={{padding:'6px 8px',cursor:'pointer',borderRadius:'4px',fontSize:'13px',
                background: selectedFile === f ? '#5a6fd6' : 'transparent',
                color: selectedFile === f ? 'white' : '#334155'
              }}>
              📄 {f}
            </div>
          ))}
        </div>
        <textarea
          value={content}
          onChange={handleContentChange}
          style={{flex:1,padding:'16px',fontFamily:'monospace',fontSize:'14px',border:'none',outline:'none',resize:'none'}}
          placeholder="Select a file to edit..."
        />
        {pdfUrl && (
          <div style={{flex:1,borderLeft:'1px solid #e2e8f0',display:'flex',flexDirection:'column'}}>
            <div style={{padding:'8px 16px',background:'#f8fafc',borderBottom:'1px solid #e2e8f0',fontWeight:'600',color:'#475569'}}>PDF Preview</div>
            <iframe src={pdfUrl} style={{flex:1,border:'none'}} title="PDF Preview" />
          </div>
        )}
      </div>
    </div>
  );
}

export default SharedProject;
